import React, { useState, useEffect } from "react";
import { StyleSheet, ScrollView, View, Pressable, Platform } from "react-native";
import { router, usePathname } from "expo-router";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import Input from "@/components/ui/Input";
import { DateInput } from "@/components/ui/DateInput";
import { useTheme } from "@/hooks/useTheme";
import { MaterialIcons } from "@expo/vector-icons";
import { useCallRequest } from "@/contexts/CallRequestContext";
import type { CallDetail, CallPosition } from "@/types/contexts/callRequest";
import { callRequestService } from "@/services/callRequestService";

const STEP_INDEX = 1;

function formatDescription(formData: any): string {
  const lines: string[] = [];
  lines.push("CALL REQUEST");
  lines.push("=============");
  lines.push(`Company: ${formData.companyName}`);
  lines.push(`Submitter Email: ${formData.submitterEmail}`);
  lines.push(`Job Name: ${formData.jobName}`);

  if (formData.jobStartDate || formData.jobEndDate) {
    const start = formData.jobStartDate ? new Date(formData.jobStartDate).toLocaleDateString() : "N/A";
    const end = formData.jobEndDate ? new Date(formData.jobEndDate).toLocaleDateString() : "N/A";
    lines.push(`Job Dates: ${start} - ${end}`);
  }

  if (formData.venueName) lines.push(`Venue: ${formData.venueName}`);
  if (formData.venueAddress) lines.push(`Venue Address: ${formData.venueAddress}`);
  if (formData.mealOption) lines.push(`Meal Option: ${formData.mealOption}`);
  if (formData.indoorOutdoor) lines.push(`Indoor/Outdoor: ${formData.indoorOutdoor}`);
  if (formData.multipleLocations) lines.push(`Multiple Locations: ${formData.multipleLocations}`);
  if (formData.preferredUniform) lines.push(`Preferred Uniform: ${formData.preferredUniform}`);

  if (formData.onsiteContactName || formData.onsiteContactPhone) {
    const contact = formData.onsiteContactName || "N/A";
    const phone = formData.onsiteContactPhone ? ` (${formData.onsiteContactPhone})` : "";
    lines.push(`Onsite Contact: ${contact}${phone}`);
  }

  if (formData.requestSpecificMembers === "Yes" && formData.specificMemberRequests) {
    lines.push(`Specific Team Members: ${formData.specificMemberRequests}`);
  } else if (formData.requestSpecificMembers) {
    lines.push(`Specific Team Members: ${formData.requestSpecificMembers}`);
  }

  if (formData.calls && formData.calls.length > 0) {
    lines.push("");
    lines.push("CALLS");
    lines.push("-----");

    formData.calls.forEach((call: CallDetail, index: number) => {
      lines.push(`Call ${index + 1}: ${call.callName || "Unnamed"}`);
      const callDate = call.date ? new Date(call.date).toLocaleDateString() : "N/A";
      lines.push(
        `  Date: ${callDate} | Start: ${call.startTime || "N/A"} | Duration: ${call.estimatedDuration || "N/A"}`,
      );

      if (call.positions && call.positions.length > 0) {
        const filledPositions = call.positions.filter((p) => p.positionName);
        if (filledPositions.length > 0) {
          lines.push("  Positions:");
          filledPositions.forEach((pos) => {
            lines.push(`    - ${pos.positionName} x${pos.quantity || 1}`);
          });
        }
      }
      lines.push("");
    });
  }

  return lines.join("\n");
}

export default function CallDetailsScreen() {
  const { colors } = useTheme();
  const { formData, updateFormData, updateStep, clearSavedData, resetFormData } = useCallRequest();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pathname = usePathname();

  useEffect(() => {
    if (pathname.includes("/call-details")) {
      updateStep(STEP_INDEX);
    }
  }, [pathname, updateStep]);

  const updateCall = (index: number, updates: Partial<CallDetail>) => {
    const newCalls = [...formData.calls];
    newCalls[index] = { ...newCalls[index], ...updates };
    updateFormData({ calls: newCalls });
  };

  const addCall = () => {
    updateFormData({
      calls: [
        ...formData.calls,
        {
          callName: "",
          date: "",
          startTime: "",
          estimatedDuration: "",
          positions: [{ positionName: "", quantity: "" }],
        },
      ],
    });
  };

  const removeCall = (index: number) => {
    if (formData.calls.length <= 1) return;
    const newCalls = formData.calls.filter((_, i) => i !== index);
    updateFormData({ calls: newCalls });
  };

  const updatePosition = (callIndex: number, posIndex: number, updates: Partial<CallPosition>) => {
    const newCalls = [...formData.calls];
    const newPositions = [...newCalls[callIndex].positions];
    newPositions[posIndex] = { ...newPositions[posIndex], ...updates };
    newCalls[callIndex] = { ...newCalls[callIndex], positions: newPositions };
    updateFormData({ calls: newCalls });
  };

  const addPosition = (callIndex: number) => {
    const newCalls = [...formData.calls];
    newCalls[callIndex] = {
      ...newCalls[callIndex],
      positions: [...newCalls[callIndex].positions, { positionName: "", quantity: "" }],
    };
    updateFormData({ calls: newCalls });
  };

  const removePosition = (callIndex: number, posIndex: number) => {
    const newCalls = [...formData.calls];
    if (newCalls[callIndex].positions.length <= 1) return;
    newCalls[callIndex] = {
      ...newCalls[callIndex],
      positions: newCalls[callIndex].positions.filter((_, i) => i !== posIndex),
    };
    updateFormData({ calls: newCalls });
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);

    const source = Platform.OS === "web" ? "web" : "mobile";
    const description = formatDescription(formData);

    const result = await callRequestService.submitCallRequest(
      {
        company_name: formData.companyName,
        submitter_email: formData.submitterEmail,
        job_name: formData.jobName,
        job_start_date: formData.jobStartDate || null,
        job_end_date: formData.jobEndDate || null,
        venue_name: formData.venueName || null,
        venue_address: formData.venueAddress || null,
        meal_option: formData.mealOption || null,
        indoor_outdoor: formData.indoorOutdoor || null,
        multiple_locations: formData.multipleLocations || null,
        preferred_uniform: formData.preferredUniform || null,
        onsite_contact_name: formData.onsiteContactName || null,
        onsite_contact_phone: formData.onsiteContactPhone || null,
        request_specific_members: formData.requestSpecificMembers || null,
        specific_member_requests: formData.specificMemberRequests || null,
        calls: formData.calls,
        description,
      },
      source,
    );

    setSubmitting(false);

    if (result.success) {
      clearSavedData();
      setSubmitted(true);
    } else {
      setError(result.error || "Failed to submit call request. Please try again.");
    }
  };

  const handleStartOver = () => {
    resetFormData();
    router.replace("/call-request");
  };

  if (submitted) {
    return (
      <View style={[styles.successContainer, { backgroundColor: colors.background }]}>
        <View style={[styles.successIcon, { backgroundColor: colors.emerald?.background || "#D1FAE5" }]}>
          <MaterialIcons name="check-circle" size={64} color={colors.emerald?.text || "#059669"} />
        </View>
        <ThemedText style={[styles.successTitle, { color: colors.text }]}>Call Request Submitted!</ThemedText>
        <ThemedText style={[styles.successText, { color: colors.textSecondary }]}>
          Thank you for your request! Our team will review it and contact you soon.
        </ThemedText>
        <Button
          text="Return Home"
          icon="home"
          iconSize={18}
          iconColor="white"
          onPress={() => router.replace("/")}
          style={[styles.homeButton, { backgroundColor: colors.primary }]}
          textStyle={styles.homeButtonText}
        />
      </View>
    );
  }

  return (
    <ScrollView style={[styles.container, { backgroundColor: colors.background }]}>
      <View style={styles.content}>
        <View style={styles.header}>
          <MaterialIcons name="event" size={40} color={colors.primary} />
          <ThemedText style={[styles.title, { color: colors.text }]}>Call Details</ThemedText>
          <ThemedText style={[styles.subtitle, { color: colors.textSecondary }]}>
            Add the calls you need staffed for this job
          </ThemedText>
        </View>

        {formData.calls.map((call, callIndex) => (
          <View key={callIndex} style={[styles.callCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
            <View style={styles.callHeader}>
              <ThemedText style={[styles.callTitle, { color: colors.text }]}>Call {callIndex + 1}</ThemedText>
              {formData.calls.length > 1 && (
                <Pressable
                  onPress={() => removeCall(callIndex)}
                  style={({ pressed }) => [styles.removeButton, { opacity: pressed ? 0.7 : 1 }]}
                >
                  <MaterialIcons name="close" size={20} color={colors.ruby?.text || "#DC2626"} />
                </Pressable>
              )}
            </View>

            <ThemedText style={[styles.fieldLabel, { color: colors.text }]}>Call Name</ThemedText>
            <Input
              placeholder="e.g., Morning shift, Setup crew"
              value={call.callName}
              onChangeText={(text) => updateCall(callIndex, { callName: text })}
              icon="label"
            />

            <View style={styles.spacer} />

            <View style={styles.dateRow}>
              <View style={styles.dateField}>
                <DateInput
                  label="Date"
                  value={call.date || null}
                  mode="date"
                  onChange={(date) => updateCall(callIndex, { date: date.toISOString() })}
                  placeholder="Select date"
                />
              </View>
            </View>

            <View style={styles.spacer} />

            <ThemedText style={[styles.fieldLabel, { color: colors.text }]}>Start Time</ThemedText>
            <Input
              placeholder="e.g., 8:00 AM"
              value={call.startTime}
              onChangeText={(text) => updateCall(callIndex, { startTime: text })}
              icon="schedule"
            />

            <View style={styles.spacer} />

            <ThemedText style={[styles.fieldLabel, { color: colors.text }]}>Estimated Duration</ThemedText>
            <Input
              placeholder="e.g., 8 hours"
              value={call.estimatedDuration}
              onChangeText={(text) => updateCall(callIndex, { estimatedDuration: text })}
              icon="timer"
            />

            <View style={[styles.divider, { backgroundColor: colors.border }]} />

            <ThemedText style={[styles.positionsTitle, { color: colors.text }]}>Positions</ThemedText>

            {call.positions.map((pos, posIndex) => (
              <View key={posIndex} style={styles.positionRow}>
                <View style={styles.positionNameField}>
                  <Input
                    placeholder="Position name"
                    value={pos.positionName}
                    onChangeText={(text) => updatePosition(callIndex, posIndex, { positionName: text })}
                  />
                </View>
                <View style={styles.positionQtyField}>
                  <Input
                    placeholder="Qty"
                    value={pos.quantity}
                    onChangeText={(text) => updatePosition(callIndex, posIndex, { quantity: text })}
                    keyboardType="numeric"
                  />
                </View>
                {call.positions.length > 1 && (
                  <Pressable
                    onPress={() => removePosition(callIndex, posIndex)}
                    style={({ pressed }) => [styles.removePositionButton, { opacity: pressed ? 0.7 : 1 }]}
                  >
                    <MaterialIcons name="remove-circle-outline" size={22} color={colors.ruby?.text || "#DC2626"} />
                  </Pressable>
                )}
              </View>
            ))}

            <Pressable
              onPress={() => addPosition(callIndex)}
              style={({ pressed }) => [styles.addButton, { borderColor: colors.border, opacity: pressed ? 0.7 : 1 }]}
            >
              <MaterialIcons name="add" size={18} color={colors.primary} />
              <ThemedText style={[styles.addButtonText, { color: colors.primary }]}>Add Position</ThemedText>
            </Pressable>
          </View>
        ))}

        <Pressable
          onPress={addCall}
          style={({ pressed }) => [styles.addCallButton, { borderColor: colors.primary, opacity: pressed ? 0.7 : 1 }]}
        >
          <MaterialIcons name="add-circle-outline" size={22} color={colors.primary} />
          <ThemedText style={[styles.addCallText, { color: colors.primary }]}>Add Another Call</ThemedText>
        </Pressable>

        {error && (
          <View style={[styles.errorCard, { backgroundColor: colors.attention?.background || "#FEE2E2" }]}>
            <MaterialIcons name="error" size={20} color={colors.attention?.text || "#DC2626"} />
            <ThemedText style={[styles.errorText, { color: colors.attention?.text || "#DC2626" }]}>{error}</ThemedText>
          </View>
        )}

        <View style={styles.buttonRow}>
          <Button
            text="Back"
            icon="arrow-left"
            iconPosition="left"
            iconSize={18}
            iconColor={colors.text}
            onPress={() => router.push("/call-request")}
            style={[styles.backButton, { borderColor: colors.border }]}
            textStyle={[styles.backButtonText, { color: colors.text }]}
          />
          <Button
            text={submitting ? "Submitting..." : "Submit Request"}
            icon={submitting ? undefined : "send"}
            iconPosition="right"
            iconSize={18}
            iconColor="white"
            onPress={handleSubmit}
            enabled={!submitting}
            hoverColor={colors.complete.background}
            style={[
              styles.submitButton,
              {
                backgroundColor: !submitting ? colors.complete.border || colors.primary : colors.border,
                opacity: !submitting ? 1 : 0.5,
              },
            ]}
            textStyle={styles.submitButtonText}
          />
        </View>

        <Button
          text="Start Over"
          onPress={handleStartOver}
          style={[styles.startOverButton, { borderColor: colors.border }]}
          textStyle={[styles.startOverText, { color: colors.textSecondary }]}
        />
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    padding: 20,
    gap: 16,
  },
  header: {
    alignItems: "center",
    gap: 12,
    marginBottom: 8,
  },
  title: {
    fontSize: 28,
    fontWeight: "bold",
    textAlign: "center",
  },
  subtitle: {
    fontSize: 16,
    textAlign: "center",
    lineHeight: 24,
  },
  callCard: {
    borderRadius: 12,
    borderWidth: 1,
    padding: 16,
  },
  callHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  callTitle: {
    fontSize: 18,
    fontWeight: "700",
  },
  removeButton: {
    padding: 4,
  },
  fieldLabel: {
    fontSize: 15,
    fontWeight: "600",
    marginBottom: 8,
  },
  spacer: {
    height: 16,
  },
  dateRow: {
    flexDirection: "row",
    gap: 12,
  },
  dateField: {
    flex: 1,
  },
  divider: {
    height: 1,
    marginVertical: 16,
  },
  positionsTitle: {
    fontSize: 15,
    fontWeight: "600",
    marginBottom: 12,
  },
  positionRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 8,
  },
  positionNameField: {
    flex: 3,
  },
  positionQtyField: {
    flex: 1,
  },
  removePositionButton: {
    padding: 4,
  },
  addButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderStyle: "dashed",
    marginTop: 4,
  },
  addButtonText: {
    fontSize: 14,
    fontWeight: "500",
  },
  addCallButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    paddingVertical: 14,
    borderRadius: 12,
    borderWidth: 2,
    borderStyle: "dashed",
  },
  addCallText: {
    fontSize: 16,
    fontWeight: "600",
  },
  errorCard: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    padding: 12,
    borderRadius: 8,
  },
  errorText: {
    flex: 1,
    fontSize: 14,
  },
  buttonRow: {
    flexDirection: "row",
    gap: 12,
  },
  backButton: {
    flex: 1,
    borderRadius: 8,
    minHeight: 52,
    borderWidth: 1,
    backgroundColor: "transparent",
  },
  backButtonText: {
    fontSize: 16,
    fontWeight: "600",
  },
  submitButton: {
    flex: 2,
    borderRadius: 8,
    minHeight: 52,
  },
  submitButtonText: {
    color: "white",
    fontSize: 16,
    fontWeight: "600",
  },
  startOverButton: {
    borderRadius: 8,
    minHeight: 44,
    borderWidth: 1,
    backgroundColor: "transparent",
  },
  startOverText: {
    fontSize: 14,
  },
  successContainer: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
    gap: 20,
  },
  successIcon: {
    width: 120,
    height: 120,
    borderRadius: 60,
    alignItems: "center",
    justifyContent: "center",
  },
  successTitle: {
    fontSize: 28,
    fontWeight: "bold",
    textAlign: "center",
  },
  successText: {
    fontSize: 16,
    textAlign: "center",
    lineHeight: 24,
  },
  homeButton: {
    borderRadius: 8,
    minHeight: 52,
    minWidth: 200,
    marginTop: 8,
  },
  homeButtonText: {
    color: "white",
    fontSize: 16,
    fontWeight: "600",
  },
});
